const API_URL = process.env.REACT_APP_API_URL;

const cleanAuthToken = () => {
  const rawToken = localStorage.getItem('auth_token');
  if (!rawToken || typeof rawToken !== 'string') {
    return null;
  }
  const token = rawToken.replace(/\r?\n/g, '').trim();
  if (!token || /[\u0000-\u001F\u007F]/.test(token)) {
    return null;
  }
  return token;
};

const authHeader = () => {
  const token = cleanAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : null;
};

// Scrapper API
export const getScrapperData = async (filters) => {
  try {
    const response = await fetch(`${API_URL}/scrapper/scrapper/data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters || {})
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP Error: ${response.status}`);
    }
    return await response.json();
  } catch (err) {
    console.error('API Error:', err);
    throw err;
  }
};

// Login API
export const loginEmployee = async ({ username, password }) => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// ============================================================
// ROLE-BASED LOGIN APIs
// ============================================================

// Role-Based Login (RECRUITER, HR, ADMIN)
export const roleBasedLogin = async ({ email, password }) => {
  try {
    console.log('🔐 API roleBasedLogin: Starting login for', email);
    const response = await fetch(`${API_URL}/recruitment-auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    console.log('📥 API: Response status:', response.status);
    console.log('📥 API: Response headers:', Object.fromEntries(response.headers));
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API: Login failed -', error);
      return { success: false, message: error.message || 'Login failed' };
    }
    const data = await response.json();
    console.log('✅ API: Login successful response:', data);
    console.log('🔑 API: Token in response:', data?.token?.substring(0, 50) + '...');
    console.log('👤 API: User data:', data?.user);
    return { success: true, data };
  } catch (err) {
    console.error('💥 API roleBasedLogin error:', err);
    return { success: false, message: 'Network error' };
  }
};

// Role-Based Register
export const roleBasedRegister = async ({ username, email, password, first_name, last_name, role = 'RECRUITER' }) => {
  try {
    const response = await fetch(`${API_URL}/recruitment-auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, first_name, last_name, role })
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

export const getUserRoles = async () => {
  try {
    const token = cleanAuthToken();
    if (!token) {
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/roles`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      }
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }

    const data = await response.json();
    return { success: true, data: data.roles || [] };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

export const createUser = async ({ username, email, password, first_name, last_name, role }) => {
  try {
    console.log('🔵 [API] createUser: Starting user creation');
    console.log('👤 [API] User data:', { username, email, password: '***', first_name, last_name, role });
    
    const token = cleanAuthToken();
    if (!token) {
      console.error('⚠️ [API] No valid token found');
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    console.log('✅ [API] Token valid, making request to:', `${API_URL}/recruitment-auth/create-user`);

    const response = await fetch(`${API_URL}/recruitment-auth/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      },
      body: JSON.stringify({ username, email, password, first_name, last_name, role })
    });

    console.log('📡 [API] Response status:', response.status);
    console.log('📡 [API] Response headers:', Object.fromEntries(response.headers));

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
        console.error('❌ [API] Server error response:', errorData);
      } catch (e) {
        console.error('❌ [API] Could not parse error response as JSON');
        errorData = { message: 'Server error: ' + response.statusText };
      }
      return { success: false, message: errorData.message || 'Failed to create user' };
    }

    const data = await response.json();
    console.log('✅ [API] User created successfully:', data);
    return { success: true, data };
  } catch (err) {
    console.error('💥 [API] createUser error:', err.message);
    console.error('💥 [API] Error stack:', err.stack);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Verify Token
export const verifyToken = async (token) => {
  try {
    const cleanToken = token ? token.replace(/\r?\n/g, '').trim() : null;
    if (!cleanToken || /[\u0000-\u001F\u007F]/.test(cleanToken)) {
      return { success: false };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/verify-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${cleanToken}`
      }
    });
    if (!response.ok) {
      return { success: false };
    }
    const data = await response.json();
    return { success: true, user: data.user };
  } catch (err) {
    return { success: false };
  }
};

// Get Recruiter Candidates
export const getRecruiterCandidates = async (userId, token) => {
  try {
    const cleanToken = token ? token.replace(/\r?\n/g, '').trim() : null;
    if (!cleanToken) {
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/recruiter/${userId}/candidates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${cleanToken}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, candidates: data.candidates };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// Get All Candidates (HR endpoint)
export const getAllCandidatesHR = async (token) => {
  try {
    const cleanToken = token ? token.replace(/\r?\n/g, '').trim() : null;
    if (!cleanToken) {
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/hr/candidates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${cleanToken}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, candidates: data.candidates };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// Forgot Password API
export const forgotPassword = async ({ email }) => {
  try {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// Candidate Registration API
export const registerCandidate = async (formData) => {
  console.log("🔄 Starting candidate registration API call");
  try {
    console.log("📤 Sending POST request to /recruitment/candidate");
    const response = await fetch(`${API_URL}/recruitment/candidate`, {
      method: 'POST',
      body: formData
    });
    console.log("📥 Response status:", response.status);
    if (!response.ok) {
      const error = await response.json();
      console.log("❌ API error response:", error);
      return { success: false, message: error.message };
    }
    const data = await response.json();
    console.log("✅ API success response:", data);
    return { success: data.success, data: data, message: data.message };
  } catch (err) {
    console.log("💥 Network error in registerCandidate:", err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Candidate API
export const updateCandidate = async (candidateId, formData) => {
  console.log("🔄 Starting candidate update API call");
  try {
    console.log("📤 Sending PUT request to /recruitment/candidate/" + candidateId);
    console.log("📦 FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(`   ${key}: ${value}`);
    }
    const token = cleanAuthToken();
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}`, {
      method: 'PUT',
      headers: {
        ...authHeader()
      },
      body: formData
    });
    console.log("📥 Response status:", response.status);
    if (!response.ok) {
      const error = await response.json();
      console.log("❌ API error response:", error);
      return { success: false, message: error.message };
    }
    const data = await response.json();
    console.log("✅ API success response:", data);
    return { success: data.success, data: data, message: data.message };
  } catch (err) {
    console.log("💥 Network error in updateCandidate:", err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Candidates List API
export const getCandidates = async () => {
  try {
    const token = cleanAuthToken();
    console.log('🔐 Token from localStorage:', token ? '✅ Found' : '❌ NOT FOUND');
    
    if (!token) {
      console.error('❌ No authentication token found in localStorage or token invalid');
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    console.log('📡 Fetching candidates from:', `${API_URL}/recruitment/candidates`);
    console.log('🔑 Authorization header:', `Bearer ${token.substring(0, 20)}...`);

    const response = await fetch(`${API_URL}/recruitment/candidates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      }
    });
    
    console.log('📊 Response status:', response.status);
    console.log('📋 Response headers:', Object.fromEntries(response.headers));

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API Error Response:', error);
      return { success: false, message: error.message || 'Failed to fetch candidates' };
    }
    
    const data = await response.json();
    console.log('✅ Candidates fetched successfully:', data);
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getCandidates:', err);
    console.error('Error details:', err.message, err.stack);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Candidate Feedback API
export const getCandidateFeedback = async (candidateId) => {
  try {
    const token = cleanAuthToken();
    
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/feedback`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getCandidateFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get All Feedback API
export const getAllFeedback = async () => {
  try {
    const token = cleanAuthToken();
    
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/feedback`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch all feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getAllFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Create Feedback API
export const createFeedback = async ({ candidateId, comment, status }) => {
  try {
    const token = cleanAuthToken();
    
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader()
      },
      body: JSON.stringify({ candidateId, comment, status })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to create feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in createFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get feedback by candidate ID
export const getFeedbackByCandidateId = async (candidateId) => {
  try {
    console.log('📥 API getFeedbackByCandidateId: Fetching feedback for candidate', candidateId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/feedback`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getFeedbackByCandidateId:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Feedback API
export const updateFeedback = async (feedbackId, { comment, status }) => {
  try {
    console.log('🔄 API updateFeedback: Updating feedback', feedbackId);
    const response = await fetch(`${API_URL}/recruitment/feedback/${feedbackId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment, status })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to update feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in updateFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Delete Feedback API
export const deleteFeedback = async (feedbackId) => {
  try {
    console.log('🗑️ API deleteFeedback: Deleting feedback', feedbackId);
    const response = await fetch(`${API_URL}/recruitment/feedback/${feedbackId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to delete feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in deleteFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};